import { SUPPLY_IDS, type SupplyId } from "@frontier/content/supplies";
import type { EquipmentKey } from "@frontier/content/arsenal";
import { z } from "zod";
import { useState } from "react";
import type { SupplyStationView } from "@frontier/protocol/supplies";
import { EquipmentArt } from "./EquipmentArt";
import { ResourceSymbol } from "./ResourceSymbol";
import { credits } from "./ui-labels";
import "./station-pages.css";
import "./supplies.css";

export interface SupplyPanelProps {
  view: SupplyStationView | null;
  busy: boolean;
  pending: boolean;
  recovery?: boolean;
  onRecover?: () => void;
  onBuy: (supplyId: SupplyId, quantity: number) => void;
  onTransfer: (
    supplyId: SupplyId,
    direction: "load" | "unload",
    quantity: number,
  ) => void;
  onRetry: () => void;
  onReload: () => void;
}
export function SupplyPanel({
  view,
  busy,
  pending,
  recovery = false,
  onRecover,
  onBuy,
  onTransfer,
  onRetry,
  onReload,
}: SupplyPanelProps) {
  const [quantity, setQuantity] = useState(5);
  const [supplyId, setSupplyId] = useState<SupplyId>("battery");
  const recoveryAction = recovery && <div className="supply-recovery">
    <span>Осталась неподтверждённая операция с припасами.</span>
    <button className="station-button station-button-gold" disabled={busy} onClick={onRecover}>Проверить запас и продолжить</button>
  </div>;
  if (!view)
    return (
      <section className="station-page supply-panel">
        <h2>Припасы</h2>
        {recoveryAction}
        <button className="station-button" onClick={onReload}>
          Загрузить запасы
        </button>
        {pending && (
          <button className="station-button" disabled={busy} onClick={onRetry}>
            Проверить операцию
          </button>
        )}
      </section>
    );
  const item = view.items.find((entry) => entry.supplyId === supplyId);
  if (!item) return null;
  const terms = item.terms;
  const art: EquipmentKey =
    item.kind === "rocket"
      ? { kind: "weapon", id: "missile" }
      : {
          kind: "passive",
          id: item.kind === "drone" ? "repair-drone" : "capacitor",
        };
  return (
    <section className="station-page supply-panel" aria-label="Припасы">
      <header className="station-page-heading">
        <div>
          <span className="station-page-kicker">ПОДГОТОВКА К ВЫЛЕТУ</span>
          <h2>Припасы</h2>
          <p>
            5 одинаковых припасов — 1 ячейка. При гибели выпадает всё
            загруженное.
          </p>
        </div>
        <div className="supply-hold">
          <span>ЗАНЯТО В ТРЮМЕ</span>
          <b>
            {view.hold.usedSlots}
            <small> / {view.hold.capacity}</small>
          </b>
          <progress
            aria-label="Занятые ячейки трюма"
            value={view.hold.usedSlots}
            max={view.hold.capacity}
          />
        </div>
      </header>
      <div className="supply-layout">
        <article className="supply-item">
          <label className="supply-picker">
            ПРИПАС
            <select
              aria-label="Тип припаса"
              value={supplyId}
              disabled={busy || pending || recovery}
              onChange={(event) =>
                setSupplyId(z.enum(SUPPLY_IDS).parse(event.target.value))
              }
            >
              {view.items.map((entry) => (
                <option key={entry.supplyId} value={entry.supplyId}>
                  {entry.name}
                </option>
              ))}
            </select>
          </label>
          <div className="supply-item-preview">
            <span className="supply-art">
              <EquipmentArt equipment={art} />
            </span>
            <div>
              <small>РАСХОДНЫЙ ПРИПАС</small>
              <strong>{item.name}</strong>
            </div>
          </div>
          <p className="supply-effect">
            {terms.algorithm === "battery-v1"
              ? `+${terms.energy} энергии`
              : terms.algorithm === "rocket-v1"
                ? `${terms.damage} урона · требуется ракетница`
                : `${terms.healingPerSecond} корпуса/с · ${terms.duration} с`}
          </p>
          <span className="supply-cooldown">
            Перезарядка {terms.cooldownSeconds} с
          </span>
          <div className="supply-unit-price">
            <span>СТОИМОСТЬ / ШТ.</span>
            <b>
              <ResourceSymbol kind="credits" />
              {credits(terms.unitCost.credits)} КР
            </b>
            <small>
              + {terms.unitCost.components.toLocaleString("ru-RU")} комп.
            </small>
          </div>
        </article>
        <section className="supply-logistics" aria-label="Склад и трюм">
          <div className="supply-logistics-heading">
            <div>
              <span className="station-page-kicker">СКЛАД → КОРАБЛЬ</span>
              <h3>Подготовь запас</h3>
            </div>
            <label>
              Количество
              <select
                aria-label="Количество припасов"
                disabled={busy || pending || recovery}
                value={quantity}
                onChange={(event) => setQuantity(Number(event.target.value))}
              >
                {[1, 5, 10, 20].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="supply-stock-cards">
            <div>
              <span>НА СКЛАДЕ</span>
              <b>{item.stock}</b>
            </div>
            <div>
              <span>В ТРЮМЕ</span>
              <b>{item.loaded}</b>
            </div>
          </div>
          <div className="supply-actions">
            <button
              className="station-button station-button-primary"
              disabled={busy || pending || recovery || item.stock < quantity}
              onClick={() => onTransfer(supplyId, "load", quantity)}
            >
              В трюм ↓
            </button>
            <button
              className="station-button"
              disabled={busy || pending || recovery || item.loaded < quantity}
              onClick={() => onTransfer(supplyId, "unload", quantity)}
            >
              На склад ↑
            </button>
          </div>
          <div className="supply-purchase">
            <div>
              <span>ПОКУПКА · {quantity} ШТ.</span>
              <b>
                {credits(
                  (
                    BigInt(terms.unitCost.credits) * BigInt(quantity)
                  ).toString(),
                )}{" "}
                КР{" "}
                <small>
                  +{" "}
                  {(terms.unitCost.components * quantity).toLocaleString(
                    "ru-RU",
                  )}{" "}
                  комп.
                </small>
              </b>
            </div>
            <button
              className="station-button station-button-gold"
              disabled={busy || pending || recovery}
              onClick={() => onBuy(supplyId, quantity)}
            >
              Купить на склад
            </button>
          </div>
        </section>
      </div>
      {recoveryAction}
      {pending && (
        <div className="supply-recovery">
          <span>Результат операции неизвестен.</span>
          <button
            className="station-button station-button-gold"
            disabled={busy}
            onClick={onRetry}
          >
            Проверить операцию
          </button>
        </div>
      )}
    </section>
  );
}
